/**
 * useRealtimeVoice — Real-time Voice Hook with VAD
 *
 * Pipeline: Voxtral Realtime ASR → GPT-5.4-nano → TTS
 * Sub-500ms latency with client-side VAD for turn detection.
 *
 * Features:
 * - Client-side VAD (Voice Activity Detection)
 * - WebSocket for full-duplex communication
 * - Barge-in support (interrupt while AI speaks)
 * - Streaming audio playback
 */
import { useState, useRef, useCallback, useEffect } from "react";
import { createVAD, type VADState } from "@/lib/vad";

export type RealtimeVoiceStatus =
  | "idle"
  | "connecting"
  | "listening"
  | "processing"
  | "speaking"
  | "error";

export interface RealtimeVoiceTranscriptEntry {
  id: string;
  role: "user" | "assistant";
  text: string;
  isFinal: boolean;
}

export interface RealtimeVoiceHook {
  status: RealtimeVoiceStatus;
  isSpeaking: boolean;
  micMuted: boolean;
  transcript: RealtimeVoiceTranscriptEntry[];
  error: string | null;
  start: (options?: { clientId?: string; voiceId?: string }) => Promise<void>;
  stop: () => void;
  toggleMic: () => void;
  interrupt: () => void;
  setOutputVolume: (volume: number) => void;
}

const PLAYBACK_SAMPLE_RATE = 24000;
const BYTES_PER_SAMPLE = 4;
const WS_CONNECT_TIMEOUT_MS = 10000;
const SESSION_START_TIMEOUT_MS = 15000;

class PcmPlayer {
  private ctx: AudioContext;
  private gainNode: GainNode;
  private node: ScriptProcessorNode;
  private inputRate: number;
  private chunks: Float32Array[] = [];
  private chunkOffset = 0;
  private bufferedSamples = 0;
  private started = false;
  private startupSamples = 0;
  private carry: Uint8Array = new Uint8Array(0);

  constructor(inputSampleRate: number) {
    this.inputRate = inputSampleRate;
    this.ctx = new AudioContext();
    this.gainNode = this.ctx.createGain();
    this.gainNode.gain.value = 1;
    this.node = this.ctx.createScriptProcessor(2048, 1, 1);
    this.startupSamples = Math.floor(this.ctx.sampleRate * 0.05);

    this.node.onaudioprocess = event => {
      const out = event.outputBuffer.getChannelData(0);
      let writePos = 0;

      if (!this.started) {
        if (this.bufferedSamples < this.startupSamples) {
          out.fill(0);
          return;
        }
        this.started = true;
      }

      while (writePos < out.length && this.chunks.length > 0) {
        const head = this.chunks[0];
        const remaining = head.length - this.chunkOffset;
        const needed = out.length - writePos;
        const toCopy = Math.min(remaining, needed);

        out.set(
          head.subarray(this.chunkOffset, this.chunkOffset + toCopy),
          writePos
        );
        writePos += toCopy;
        this.chunkOffset += toCopy;
        this.bufferedSamples -= toCopy;

        if (this.chunkOffset >= head.length) {
          this.chunks.shift();
          this.chunkOffset = 0;
        }
      }

      if (writePos < out.length) {
        out.fill(0, writePos);
      }

      if (this.bufferedSamples <= 0) {
        this.bufferedSamples = 0;
        this.started = false;
      }
    };

    this.node.connect(this.gainNode);
    this.gainNode.connect(this.ctx.destination);
  }

  private resampleLinear(
    input: Float32Array,
    outputRate: number
  ): Float32Array {
    if (this.inputRate === outputRate) return input;
    const ratio = this.inputRate / outputRate;
    const outLength = Math.max(1, Math.floor(input.length / ratio));
    const out = new Float32Array(outLength);
    for (let i = 0; i < outLength; i++) {
      const sourceIndex = i * ratio;
      const left = Math.floor(sourceIndex);
      const right = Math.min(left + 1, input.length - 1);
      const frac = sourceIndex - left;
      out[i] = input[left] * (1 - frac) + input[right] * frac;
    }
    return out;
  }

  feed(pcmBuffer: ArrayBuffer): void {
    if (this.ctx.state === "suspended") {
      void this.ctx.resume();
    }

    let incoming = new Uint8Array(pcmBuffer);
    let bytes: Uint8Array;

    if (this.carry.length > 0) {
      bytes = new Uint8Array(this.carry.length + incoming.length);
      bytes.set(this.carry);
      bytes.set(incoming, this.carry.length);
    } else {
      bytes = incoming.slice();
    }

    const usable = bytes.length - (bytes.length % BYTES_PER_SAMPLE);
    this.carry = bytes.slice(usable);
    if (usable === 0) return;

    const float32 = new Float32Array(
      bytes.buffer,
      bytes.byteOffset,
      usable / BYTES_PER_SAMPLE
    );
    const converted = this.resampleLinear(float32, this.ctx.sampleRate);
    this.chunks.push(converted);
    this.bufferedSamples += converted.length;
  }

  get isPlaying(): boolean {
    return this.bufferedSamples > 0;
  }

  clear(): void {
    this.chunks = [];
    this.chunkOffset = 0;
    this.bufferedSamples = 0;
    this.started = false;
    this.carry = new Uint8Array(0);
  }

  setVolume(volume: number): void {
    this.gainNode.gain.value = Math.max(0, Math.min(1, volume));
  }

  close(): void {
    this.clear();
    try {
      this.node.disconnect();
    } catch {}
    try {
      this.gainNode.disconnect();
    } catch {}
    void this.ctx.close();
  }
}

const MIC_SAMPLE_RATE = 16000;

async function createAudioWorkletNode(
  ctx: AudioContext,
  onAudio: (pcm: Int16Array) => void
): Promise<AudioWorkletNode> {
  try {
    await ctx.audioWorklet.addModule("/pcm-audio-processor.js");
    const node = new AudioWorkletNode(ctx, "pcm-audio-processor");
    node.port.onmessage = event => {
      if (event.data?.data) {
        onAudio(new Int16Array(event.data.data));
      }
    };
    return node;
  } catch {
    const node = ctx.createScriptProcessor(4096, 1, 1);
    node.onaudioprocess = event => {
      const input = event.inputBuffer.getChannelData(0);
      const pcm = new Int16Array(input.length);
      for (let i = 0; i < input.length; i++) {
        pcm[i] = Math.max(
          -32768,
          Math.min(32767, Math.floor(input[i] * 32768))
        );
      }
      onAudio(pcm);
    };
    return node as unknown as AudioWorkletNode;
  }
}

function createId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `rt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function int16ArrayToBase64(arr: Int16Array): string {
  const bytes = new Uint8Array(arr.buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function useRealtimeVoice(): RealtimeVoiceHook {
  const [status, setStatus] = useState<RealtimeVoiceStatus>("idle");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [micMuted, setMicMuted] = useState(false);
  const [transcript, setTranscript] = useState<RealtimeVoiceTranscriptEntry[]>(
    []
  );
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const playerRef = useRef<PcmPlayer | null>(null);
  const micCtxRef = useRef<AudioContext | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const workletRef = useRef<AudioWorkletNode | null>(null);
  const vadRef = useRef<ReturnType<typeof createVAD> | null>(null);
  const sessionActiveRef = useRef(false);
  const sessionIdRef = useRef(0);
  const speakingCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const micMutedRef = useRef(false);
  const wsConnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const sessionStartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(() => {
    micMutedRef.current = micMuted;
  }, [micMuted]);

  const closeResources = useCallback(() => {
    sessionActiveRef.current = false;

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    if (vadRef.current) {
      vadRef.current.stop();
      vadRef.current = null;
    }

    if (workletRef.current) {
      try {
        workletRef.current.disconnect();
      } catch {}
      workletRef.current = null;
    }

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(t => t.stop());
      micStreamRef.current = null;
    }

    if (micCtxRef.current) {
      void micCtxRef.current.close();
      micCtxRef.current = null;
    }

    if (playerRef.current) {
      playerRef.current.close();
      playerRef.current = null;
    }

    if (speakingCheckRef.current) {
      clearInterval(speakingCheckRef.current);
      speakingCheckRef.current = null;
    }

    if (wsConnectTimeoutRef.current) {
      clearTimeout(wsConnectTimeoutRef.current);
      wsConnectTimeoutRef.current = null;
    }

    if (sessionStartTimeoutRef.current) {
      clearTimeout(sessionStartTimeoutRef.current);
      sessionStartTimeoutRef.current = null;
    }

    setIsSpeaking(false);
  }, []);

  useEffect(() => {
    return () => {
      sessionIdRef.current += 1;
      closeResources();
    };
  }, [closeResources]);

  const addTranscript = useCallback(
    (role: "user" | "assistant", text: string, isFinal: boolean) => {
      if (!text.trim()) return;
      setTranscript(prev => {
        const last = prev[prev.length - 1];
        if (last && last.role === role && !last.isFinal && !isFinal) {
          return [...prev.slice(0, -1), { ...last, text }];
        }
        if (last && last.role === role && !last.isFinal && isFinal) {
          return [...prev.slice(0, -1), { ...last, text, isFinal: true }];
        }
        return [...prev, { id: createId(), role, text, isFinal }];
      });
    },
    []
  );

  const start = useCallback(
    async (options?: { clientId?: string; voiceId?: string }) => {
      if (sessionActiveRef.current) return;

      const sessionId = sessionIdRef.current + 1;
      sessionIdRef.current = sessionId;
      sessionActiveRef.current = true;

      setStatus("connecting");
      setError(null);
      setTranscript([]);
      setIsSpeaking(false);
      setMicMuted(false);

      try {
        const voiceId = options?.voiceId || "annam";
        const clientId = options?.clientId || "anonymous-client";

        const tokenRes = await fetch(
          `/api/voice/realtime-token?clientId=${encodeURIComponent(clientId)}&voiceId=${encodeURIComponent(voiceId)}`,
          { credentials: "include" }
        );

        if (!tokenRes.ok) {
          throw new Error("Failed to get session token");
        }

        const { token } = await tokenRes.json();

        if (sessionId !== sessionIdRef.current) return;

        const wsProtocol =
          window.location.protocol === "https:" ? "wss:" : "ws:";
        const wsUrl = `${wsProtocol}//${window.location.host}/api/voice/realtime?token=${token}`;

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        console.log(
          "[RealtimeVoice] WebSocket created, readyState:",
          ws.readyState
        );

        ws.onopen = () => {
          console.log(
            "[RealtimeVoice] WebSocket opened, sending start message"
          );
          if (sessionId !== sessionIdRef.current) {
            console.log(
              "[RealtimeVoice] Session ID mismatch, not sending start"
            );
            return;
          }
          if (wsConnectTimeoutRef.current) {
            clearTimeout(wsConnectTimeoutRef.current);
            wsConnectTimeoutRef.current = null;
          }
          playerRef.current = new PcmPlayer(PLAYBACK_SAMPLE_RATE);

          sessionStartTimeoutRef.current = setTimeout(() => {
            if (sessionId !== sessionIdRef.current) return;
            setError("Sessionstart fehlgeschlagen. Bitte erneut versuchen.");
            setStatus("error");
            closeResources();
          }, SESSION_START_TIMEOUT_MS);

          // Send start message to begin ASR
          console.log("[RealtimeVoice] Sending start message");
          ws.send(JSON.stringify({ type: "start" }));
          console.log("[RealtimeVoice] Start message sent");
        };

        ws.onmessage = event => {
          if (sessionId !== sessionIdRef.current) return;

          try {
            const msg = JSON.parse(event.data);

            switch (msg.type) {
              case "state":
                if (sessionStartTimeoutRef.current) {
                  clearTimeout(sessionStartTimeoutRef.current);
                  sessionStartTimeoutRef.current = null;
                }
                setStatus(msg.state);
                if (msg.state === "listening") {
                  setIsSpeaking(false);
                }
                // Send start message when server is ready (idle state)
                if (
                  msg.state === "idle" &&
                  wsRef.current?.readyState === WebSocket.OPEN
                ) {
                  console.log(
                    "[RealtimeVoice] Server ready (idle), sending start message"
                  );
                  wsRef.current.send(JSON.stringify({ type: "start" }));
                }
                break;

              case "transcript":
                addTranscript("user", msg.text, msg.isFinal);
                break;

              case "ai_text":
                console.log(
                  "[RealtimeVoice] Received ai_text:",
                  msg.text?.slice(0, 50),
                  "isFinal:",
                  msg.isFinal
                );
                addTranscript("assistant", msg.text, msg.isFinal ?? false);
                break;

              case "audio":
                if (playerRef.current && msg.audio) {
                  const pcmBuffer = Uint8Array.from(atob(msg.audio), c =>
                    c.charCodeAt(0)
                  ).buffer;
                  playerRef.current.feed(pcmBuffer);
                  setIsSpeaking(true);
                }
                break;

              case "done":
                setIsSpeaking(false);
                setTranscript(prev => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last && last.role === "assistant" && !last.isFinal) {
                    updated[updated.length - 1] = { ...last, isFinal: true };
                  }
                  return updated;
                });
                break;

              case "interrupted":
              case "interrupting_ai":
                console.log(
                  "[RealtimeVoice] Received interrupting_ai, stopping audio"
                );
                // Stop audio immediately
                if (playerRef.current) {
                  playerRef.current.clear();
                }
                setIsSpeaking(false);
                break;

              case "error":
                if (sessionStartTimeoutRef.current) {
                  clearTimeout(sessionStartTimeoutRef.current);
                  sessionStartTimeoutRef.current = null;
                }
                setError(msg.error);
                setStatus("error");
                break;
            }
          } catch (err) {
            console.error("[RealtimeVoice] Failed to parse message:", err);
          }
        };

        ws.onerror = () => {
          console.error("[RealtimeVoice] WebSocket error");
          if (wsConnectTimeoutRef.current) {
            clearTimeout(wsConnectTimeoutRef.current);
            wsConnectTimeoutRef.current = null;
          }
          if (sessionStartTimeoutRef.current) {
            clearTimeout(sessionStartTimeoutRef.current);
            sessionStartTimeoutRef.current = null;
          }
          setError("Connection error");
          setStatus("error");
        };

        ws.onclose = () => {
          if (wsConnectTimeoutRef.current) {
            clearTimeout(wsConnectTimeoutRef.current);
            wsConnectTimeoutRef.current = null;
          }
          if (sessionStartTimeoutRef.current) {
            clearTimeout(sessionStartTimeoutRef.current);
            sessionStartTimeoutRef.current = null;
          }
          if (sessionActiveRef.current && sessionId === sessionIdRef.current) {
            setStatus("error");
            setError("Connection closed unexpectedly");
          }
        };

        // Get microphone access
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            sampleRate: MIC_SAMPLE_RATE,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });

        if (sessionId !== sessionIdRef.current) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }

        micStreamRef.current = stream;

        // Initialize VAD with human-like sensitivity
        const vad = createVAD(
          {
            energyThreshold: 0.025, // Higher threshold (less sensitive)
            speechStartDelay: 400, // 400ms sustained speech required
            speechEndDelay: 600, // Natural pause detection
            smoothing: 0.9,
          },
          {
            onSpeechStart: () => {
              // Don't trigger if AI is currently speaking (prevents echo)
              if (isSpeaking) return;
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: "speech_start" }));
              }
            },
            onSpeechEnd: () => {
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: "speech_end" }));
              }
            },
          }
        );

        await vad.initialize(stream);
        vadRef.current = vad;
        vad.start();

        // Set up audio processing
        const micCtx = new AudioContext({ sampleRate: MIC_SAMPLE_RATE });
        micCtxRef.current = micCtx;

        const source = micCtx.createMediaStreamSource(stream);
        const worklet = await createAudioWorkletNode(micCtx, pcm => {
          if (
            sessionActiveRef.current &&
            wsRef.current?.readyState === WebSocket.OPEN &&
            !micMutedRef.current
          ) {
            wsRef.current.send(
              JSON.stringify({
                type: "audio",
                audio: int16ArrayToBase64(pcm),
              })
            );
          }
        });
        workletRef.current = worklet;

        source.connect(worklet);
      } catch (err) {
        console.error("[RealtimeVoice] Start error:", err);
        setError(err instanceof Error ? err.message : "Failed to start");
        setStatus("error");
        closeResources();
      }
    },
    [addTranscript, closeResources]
  );

  const stop = useCallback(() => {
    sessionIdRef.current += 1;
    sessionActiveRef.current = false;
    setStatus("idle");
    closeResources();
  }, [closeResources]);

  const toggleMic = useCallback(() => {
    setMicMuted(prev => !prev);
  }, []);

  const interrupt = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "interrupt" }));
      if (playerRef.current) {
        playerRef.current.clear();
      }
      setIsSpeaking(false);
    }
  }, []);

  const setOutputVolume = useCallback((volume: number) => {
    playerRef.current?.setVolume(volume);
  }, []);

  return {
    status,
    isSpeaking,
    micMuted,
    transcript,
    error,
    start,
    stop,
    toggleMic,
    interrupt,
    setOutputVolume,
  };
}
